/** Google Reviews import — Place ID config, lookup and manual sync. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function GoogleReviewsImport() {
  const [cfg, setCfg] = useState(null);
  const [lookupQ, setLookupQ] = useState("Juke Transfers Split");
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/reviews/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const savePlaceId = async (place_id) => {
    setBusy(true);
    try {
      await api.patch("/admin/reviews/config", { place_id });
      setCfg((c) => ({ ...(c || {}), place_id }));
      setCandidates(null);
      toast.success("Place ID saved · reviews cache cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save Place ID");
    } finally {
      setBusy(false);
    }
  };

  const lookup = async () => {
    if (!lookupQ.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/reviews/lookup-place-id`, { params: { q: lookupQ.trim() } });
      setCandidates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Place-ID lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reviews/refresh");
      if (data.ok) toast.success(`Refreshed · ${data.reviews_count} reviews loaded`);
      else toast.error(data.detail || "Refresh failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;
  return (
    <div data-testid="reviews-config-card" className="bg-[#0d0d0d] border border-[#C5A059]/20 rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">Google Reviews import</p>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Pull 5-star reviews from your Google Business listing straight into the homepage.
            Cached for 24 hours to stay under Places API limits.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">API key</p>
          <p className={`text-sm ${cfg.has_api_key ? "text-emerald-400" : "text-red-400"}`}>
            {cfg.has_api_key ? "configured" : "missing"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Current Place ID</label>
          <Input
            data-testid="reviews-place-id-input"
            defaultValue={cfg.place_id || ""}
            key={cfg.place_id}
            placeholder="ChIJ… (paste from Google or use lookup)"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== (cfg.place_id || "")) savePlaceId(v);
            }}
            className="mt-1 bg-zinc-900 border-white/10 text-sm h-11 tabular-nums"
          />
        </div>
        <button
          type="button"
          data-testid="reviews-refresh-btn"
          disabled={busy || !cfg.place_id}
          onClick={refresh}
          className="h-11 px-5 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 text-[10px] uppercase tracking-[0.25em] disabled:opacity-40 whitespace-nowrap"
        >
          Refresh now
        </button>
      </div>

      <div className="mt-5 pt-5 border-t border-white/5">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Don&apos;t know your Place ID? Search for your business:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <Input
            data-testid="reviews-lookup-input"
            value={lookupQ}
            onChange={(e) => setLookupQ(e.target.value)}
            placeholder="Juke Transfers Split"
            className="bg-zinc-900 border-white/10 text-sm h-11"
          />
          <button
            type="button"
            data-testid="reviews-lookup-btn"
            onClick={lookup}
            disabled={busy}
            className="h-11 px-5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.25em] font-semibold whitespace-nowrap disabled:opacity-40"
          >
            Search Google
          </button>
        </div>

        {Array.isArray(candidates) && (
          <div className="mt-3 space-y-2">
            {candidates.length === 0 ? (
              <p className="text-xs text-zinc-500">No candidates found. Try a different search phrase.</p>
            ) : candidates.map((c) => (
              <button
                key={c.place_id}
                type="button"
                data-testid={`reviews-candidate-${c.place_id}`}
                onClick={() => savePlaceId(c.place_id)}
                className="w-full text-left border border-white/10 hover:border-[#C5A059] bg-zinc-950 rounded-xl px-4 py-3 transition-colors"
              >
                <p className="text-sm text-white">{c.name}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{c.formatted_address}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mt-1">
                  {c.rating ? `${c.rating} ★ (${c.user_ratings_total || 0} reviews)` : "No rating yet"} · {c.place_id}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
